import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  StretchPanel as Panel,
  useNotification,
  Flex,
  Table,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableActions,
  TableCell,
  Button,
  Checkbox,
  FixedFooter,
  FixedFooterContainer,
  Pagination,
  useModal,
  PageWrapper,
  usePaginationReducer,
  useOrderableTableReducer,
  OrderableTableHeadCell,
  PanelSlider,
  FormGroup,
  TextArea,
  Label
} from '@sopranodesign/pulse-components';
import {
  useLanguages,
  useQueryClient,
  useQuery,
  useMutation
} from '@sopranodesign/pulse-hooks';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import {
  EditUtterance,
  CreateUtterance,
  LanguageSelect
} from '../Components/index';
import {
  createUtterance as createUtteranceApi,
  updateUtterance as updateUtteranceApi,
  deleteUtterance as deleteUtteranceApi,
  deleteUtterances as deleteUtterancesApi,
  createUtterancesBulk,
  generateQuestions
} from '../api/index';
import QuestionsFooter from './QuestionsFooter';

const Questions = ({
  match: {
    params: { faqId }
  }
}) => {
  const pagination = usePaginationReducer({ id: 'faq-questions' });

  const order = useOrderableTableReducer({ id: 'faq-questions' });

  const { defaultLanguage } = useLanguages();

  // Only use showNotification for consistency

  const queryClient = useQueryClient();

  const { showModal, hideModal } = useModal();

  const { showNotification } = useNotification();

  const [editUtterance, setEditUtterance] = useState(false);

  const [creatingUtterance, setCreatingUtterance] = useState(false);

  const [language, setLanguage] = useState(defaultLanguage?.id || null);

  const [generateQuestionsPanel, setGenerateQuestionsPanel] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [savingGenerated, setSavingGenerated] = useState(false);

  const utteranceQuery = useQuery({
    queryKey: [
      `faqs/${faqId}/utterances`,
      { ...pagination.params, ...order.params, language_id: language }
    ]
  });

  const faqQuery = useQuery({
    queryKey: [`faqs/${faqId}`, { language_id: language }]
  });

  const generateQuestionsMutation = useMutation({
    mutationFn: generateQuestions,
    onSuccess: data => {
      setGeneratedQuestions(data?.data || []);
      showNotification({ message: 'Questions generated.' });
      queryClient.invalidateQueries([`faqs/${faqId}/utterances`]);
    },
    onError: error => {
      showNotification({
        message: error?.message || 'Failed to generate questions.',
        type: 'error'
      });
    }
  });
  const [selectedUtterances, setSelectedUtterances] = useState([]);

  const allItemsSelected = useMemo(() => {
    if (!utteranceQuery.isSuccess) {
      return false;
    }

    return utteranceQuery.data.data
      .map(({ id }) => id)
      .every(id => selectedUtterances.includes(id));
  }, [utteranceQuery.data, utteranceQuery.isSuccess, selectedUtterances]);

  const handleSelectingAll = () => {
    // If all items on the current paginated page are
    // selected, remove them.
    if (allItemsSelected) {
      return setSelectedUtterances(selected => [
        ...new Set(
          // Filter selected ids to not include faqs on
          // the current paginated page.
          selected.filter(id => {
            return !utteranceQuery.data.data.map(({ id }) => id).includes(id);
          })
        )
      ]);
    }

    // If not all items on the paginated page are selected,
    // select them all.
    setSelectedUtterances(selected => [
      ...new Set(selected.concat(utteranceQuery.data.data.map(({ id }) => id)))
    ]);
  };

  const { mutate: createUtterance } = useMutation({
    mutationFn: createUtteranceApi,
    onSuccess: () => {
      queryClient.invalidateQueries(`faqs/${faqId}/utterances`);
      showNotification({ message: 'Successfully created question' });
      setCreatingUtterance(false);
    }
  });

  const { mutate: updateUtterance } = useMutation({
    mutationFn: updateUtteranceApi,
    onSuccess: () => {
      queryClient.invalidateQueries(`faqs/${faqId}/utterances`);
      showNotification({ message: 'Successfully updated question' });
      setEditUtterance(false);
    }
  });

  const { mutate: deleteUtterance } = useMutation({
    mutationFn: deleteUtteranceApi,
    onSuccess: () => {
      queryClient.invalidateQueries(`faqs/${faqId}/utterances`);
      showNotification({ message: 'Successfully deleted question' });
    }
  });

  const { mutate: deleteUtterances } = useMutation({
    mutationFn: deleteUtterancesApi,
    onSuccess: () => {
      queryClient.invalidateQueries(`faqs/${faqId}/utterances`);
      showNotification({
        message: `Successfully deleted question${selectedUtterances.length > 1 ? 's' : ''}.`
      });
      setSelectedUtterances([]);
    }
  });

  const handleCreateUtterance = (event, text) => {
    event.preventDefault();
    createUtterance({
      faqId,
      utterance: {
        utterance: text,
        language_id: language
      }
    });
  };

  const handleUpdateUtterance = ({ id, text }) => {
    updateUtterance({
      faqId,
      utteranceId: id,
      utterance: {
        utterance: text,
        language_id: language
      }
    });
  };

  const handleDeleteUtterance = id => {
    showModal({
      header: 'Remove Question',
      body: `Are you sure you want to remove this question?`,
      handleConfirm: () => {
        deleteUtterance({
          faqId,
          utteranceId: id
        });
        hideModal();
      }
    });
  };

  const handleDeleteAll = () => {
    showModal({
      header: `Remove Question${selectedUtterances.length > 1 ? 's' : ''}`,
      body: `Are you sure you want to remove ${
        selectedUtterances.length > 1 ? 'these questions' : 'this question'
      }?`,
      handleConfirm: () => {
        deleteUtterances({
          faqId,
          utteranceIds: { utteranceIds: selectedUtterances }
        });
        hideModal();
      }
    });
  };

  const handleSelect = faqId => {
    const selected = selectedUtterances.includes(faqId);

    // Remove faq, or add faq.
    // Need to spread a Set to remove any duplicates in case we
    // get a high volume of clicks.
    setSelectedUtterances(ids => {
      return selected
        ? [...new Set(ids.filter(id => id !== faqId))]
        : [...new Set(ids.concat([faqId]))];
    });
  };

  const handleLanguageChange = option => {
    pagination.resetPage();
    setSelectedUtterances([]);
    setLanguage(option.value);
  };

  const generateHeader = () => {
    if (faqQuery.isLoading) {
      return 'Questions';
    }

    if (!faqQuery.data.data.location) {
      return `Questions for ${faqQuery.data.data.name}`;
    }

    return `Questions for ${faqQuery.data.data.name} (${faqQuery.data.data.location.name})`;
  };

  const handleGenerateQuestions = faq => {
    setGenerateQuestionsPanel(faq);
    setPrompt('');
    setSelectedLanguage(defaultLanguage?.id || null);
    setGeneratedQuestions([]);
  };

  const handleGenerateQuestionsSubmit = () => {
    // Get all utterances from all intents, filtered by selected language
    const utterances = (generateQuestionsPanel.intents || [])
      .flatMap(intent => intent.utterances || [])
      .filter(u => u.language?.id === selectedLanguage);

    // Get just the text of those utterances
    const questions = utterances.map(u => u.utterance);

    generateQuestionsMutation.mutate({
      faqId: generateQuestionsPanel.id,
      languageId: selectedLanguage,
      prompt,
      utterances: questions
    });
  };

  const handleSaveGenerated = async () => {
    if (!generateQuestionsPanel || generatedQuestions.length === 0) {
      return;
    }

    setSavingGenerated(true);

    try {
      await createUtterancesBulk({
        faqId,
        languageId: selectedLanguage,
        utterances: generatedQuestions
      });

      showNotification({ message: 'All generated questions saved.' });
      setGeneratedQuestions([]);
      queryClient.invalidateQueries(`faqs/${faqId}/utterances`);
      setGenerateQuestionsPanel(null);
      setPrompt('');
      setSelectedLanguage(null);
    } catch (err) {
      showNotification({
        message: err?.message || 'Failed to save questions.',
        type: 'error'
      });
    } finally {
      setSavingGenerated(false);
    }
  };

  return (
    <>
      {editUtterance && (
        <EditUtterance
          utterance={editUtterance}
          handleCancel={() => setEditUtterance(false)}
          handleSubmit={handleUpdateUtterance}
        />
      )}
      {creatingUtterance && (
        <CreateUtterance
          handleCancel={() => setCreatingUtterance(false)}
          handleSubmit={handleCreateUtterance}
        />
      )}
      <PageWrapper
        header={generateHeader()}
        subheader="Manage how an FAQ can be phrased"
        actions={
          <Flex xSpace>
            <PermissionFilter can="create faqs">
              <Button
                as={Link}
                variant="secondary"
                to={`/faqs/${faqId}/duplicate`}
                icon="clipboardCopy">
                Duplicate
              </Button>
            </PermissionFilter>
            <Button
              as={Link}
              variant="secondary"
              to={`/faqs/${faqId}/responses`}>
              Answers
            </Button>
            <Button
              variant="primary"
              icon="plus"
              onClick={() => setCreatingUtterance(true)}>
              New Question
            </Button>
            <Button
              variant="primary"
              icon="plus"
              type="button"
              onClick={() => handleGenerateQuestions(faqQuery.data.data)}>
              Generate Questions
            </Button>
          </Flex>
        }>
        <Panel mt>
          <Table loading={utteranceQuery.isLoading} loadingColumns={2}>
            <TableHead>
              <TableRow>
                <PermissionFilter can="edit faqs">
                  <TableHeadCell style={{ width: '0.5rem' }}>
                    <Flex>
                      <Checkbox
                        checked={allItemsSelected}
                        onChange={handleSelectingAll}
                        aria-label="select all"
                      />
                    </Flex>
                  </TableHeadCell>
                </PermissionFilter>
                <OrderableTableHeadCell
                  row={{ label: 'Question', sort: 'utterance' }}
                  {...order.props}
                />
                <TableHeadCell style={{ width: '10rem', fontWeight: '500' }}>
                  <LanguageSelect
                    value={language}
                    onChange={handleLanguageChange}
                  />
                </TableHeadCell>
              </TableRow>
            </TableHead>
            {utteranceQuery.isSuccess && (
              <TableBody>
                {utteranceQuery.data.data.map(({ id, utterance }, key) => (
                  <TableRow key={key}>
                    <PermissionFilter can="edit faqs">
                      <TableCell>
                        <Checkbox
                          checked={selectedUtterances.includes(id)}
                          onChange={() => {
                            handleSelect(id);
                          }}
                          aria-label={`batch-select-${key + 1}`}
                        />
                      </TableCell>
                    </PermissionFilter>
                    <TableCell>{utterance}</TableCell>
                    <TableActions>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setEditUtterance({ id, text: utterance })
                        }
                        icon="pencilAlt">
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        colour="danger"
                        icon="trash"
                        onClick={() => handleDeleteUtterance(id)}
                        disabled={utteranceQuery.data.data.length === 1}>
                        Delete
                      </Button>
                    </TableActions>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {utteranceQuery.showPagination && (
            <Pagination
              pagination={utteranceQuery.data.meta}
              {...pagination.props}
            />
          )}
        </Panel>
        {generateQuestionsPanel && (
          <PanelSlider
            header={`Generate Questions for "${generateQuestionsPanel.name}"`}
            handleHide={() => {
              setGenerateQuestionsPanel(null);
              setPrompt('');
              setSelectedLanguage(null);
              setGeneratedQuestions([]);
            }}>
            <Flex col ySpace>
              <FormGroup>
                <Label htmlFor="language">Language</Label>
                <LanguageSelect
                  value={selectedLanguage}
                  onChange={option =>
                    setSelectedLanguage(option?.value || null)
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="prompt">Prompt</Label>
                <TextArea
                  id="prompt"
                  name="prompt"
                  placeholder="Describe what kind of questions you'd like to generate for this FAQ..."
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  rows={5}
                />
              </FormGroup>
              <Button
                variant="primary"
                onClick={handleGenerateQuestionsSubmit}
                disabled={
                  !prompt.trim() ||
                  !selectedLanguage ||
                  generateQuestionsMutation.isPending
                }
                loading={generateQuestionsMutation.isPending}>
                Generate
              </Button>
              {generatedQuestions.length > 0 && (
                <Panel mt>
                  <h3 style={{ paddingLeft: '0.5rem' }}>Generated Questions</h3>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeadCell>#</TableHeadCell>
                        <TableHeadCell>Question</TableHeadCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {generatedQuestions.map((q, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{q}</TableCell>
                          <TableCell>
                            <Button
                              variant="secondary"
                              colour="danger"
                              onClick={() => {
                                setGeneratedQuestions(prev =>
                                  prev.filter((_, idx) => idx !== i)
                                );
                              }}>
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Flex right mt mb mr>
                    <Button
                      variant="primary"
                      onClick={handleSaveGenerated}
                      disabled={savingGenerated}
                      loading={savingGenerated}>
                      Save
                    </Button>
                  </Flex>
                </Panel>
              )}
            </Flex>
          </PanelSlider>
        )}
        <AnimatePresence>
          {selectedUtterances.length > 0 && (
            <FixedFooterContainer>
              <FixedFooter>
                <QuestionsFooter
                  selectedUtterances={selectedUtterances}
                  handleDelete={handleDeleteAll}
                  handleClear={() => setSelectedUtterances([])}
                />
              </FixedFooter>
            </FixedFooterContainer>
          )}
        </AnimatePresence>
      </PageWrapper>
    </>
  );
};

export default Questions;
